/**
 * BroadcastTemplatesAdmin — CRUD UI for the tenant-scoped broadcast templates
 * shipped in iter-349. Each template is a branding kit (intro/outro/logo URL,
 * lower-third style, captions style, intro text) used by:
 *   - OBS overlay pushes from BroadcastDashboard
 *   - FFmpeg.wasm exports in the in-browser VideoEditor
 *
 * Backend wiring (already live in routes/video_projects.py):
 *   - GET    /api/video/templates?tenant_id&kind
 *   - POST   /api/video/templates
 *   - PUT    /api/video/templates/{id}
 *   - DELETE /api/video/templates/{id}
 *
 * The `config` shape is free-form so admins can extend it without a backend
 * migration. We surface the most common fields in the form (logo, intro,
 * outro, lower-third color, lower-third position, captions style) and a JSON
 * editor for power users to tweak the rest.
 */
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Palette, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import { MediaUpload } from '@/components/MediaUpload';

const KINDS = [
  { value: 'broadcast', label: 'Broadcast' },
  { value: 'sermon', label: 'Sermon' },
  { value: 'event', label: 'Event' },
  { value: 'class', label: 'Class' },
  { value: 'social_clip', label: 'Social Clip' },
];

const DEFAULT_CONFIG = {
  logo_url: '',
  intro_text: '',
  outro_text: '',
  lower_third_color: '#3b82f6',
  lower_third_position: 'bottom',
  captions_style: 'standard',
};

export default function BroadcastTemplatesAdmin({ tenantId }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);  // null | {} for new | template for edit
  const [kindFilter, setKindFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['broadcast-templates', tenantId, kindFilter],
    queryFn: () => api.get('/video/templates', {
      params: { tenant_id: tenantId, ...(kindFilter ? { kind: kindFilter } : {}) },
    }).then(r => r.data),
    enabled: !!tenantId,
  });

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/video/templates/${id}`),
    onSuccess: () => {
      toast.success('Template deleted');
      qc.invalidateQueries({ queryKey: ['broadcast-templates'] });
    },
  });

  const templates = data?.templates || [];

  return (
    <div className="space-y-4" data-testid="broadcast-templates-admin">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Palette className="h-4 w-4 text-fuchsia-600" /> Broadcast Templates
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={kindFilter || 'all'} onValueChange={(v) => setKindFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full max-w-[160px]" data-testid="bt-kind-filter">
                <SelectValue placeholder="All kinds" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All kinds</SelectItem>
                {KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={() => setEditing({})} data-testid="bt-new-btn">
              <Plus className="h-4 w-4 mr-1" /> New
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-xs text-slate-500">Loading…</p>
          ) : templates.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8 border border-dashed rounded" data-testid="bt-empty">
              No broadcast templates yet. Click <strong>New</strong> to define your church/school branding kit
              (intro/outro, logo, lower-third style). Templates are scoped per tenant + per kind.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {templates.map(t => (
                <Card key={t.id} className="hover:border-fuchsia-300 transition" data-testid={`bt-card-${t.id}`}>
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-start justify-between gap-1">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{t.name}</p>
                        <Badge variant="outline" className="text-[10px] mt-0.5">{t.kind}</Badge>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Button size="icon" variant="ghost" onClick={() => setEditing(t)} data-testid={`bt-edit-${t.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => { if (window.confirm('Delete this template?')) delMut.mutate(t.id); }} data-testid={`bt-delete-${t.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      {t.config?.logo_url ? <span className="truncate">Logo: {t.config.logo_url.slice(0, 30)}…</span> : <span className="opacity-60">No logo</span>}
                      {t.config?.lower_third_color && (
                        <span className="inline-flex items-center gap-1">
                          <span className="h-2.5 w-2.5 rounded-sm border" style={{ background: t.config.lower_third_color }} />
                          LT
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <TemplateDialog
        editing={editing}
        tenantId={tenantId}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          qc.invalidateQueries({ queryKey: ['broadcast-templates'] });
        }}
      />
    </div>
  );
}

function TemplateDialog({ editing, tenantId, onClose, onSaved }) {
  const isEdit = editing && editing.id;
  const [form, setForm] = useState({ name: '', kind: 'broadcast', config: { ...DEFAULT_CONFIG } });
  const [advancedJson, setAdvancedJson] = useState('{}');

  useEffect(() => {
    if (!editing) return;
    if (editing.id) {
      const cfg = editing.config || {};
      setForm({ name: editing.name, kind: editing.kind || 'broadcast', config: { ...DEFAULT_CONFIG, ...cfg } });
      // Strip the common keys we already render fields for; rest goes to advanced JSON.
      const known = new Set(Object.keys(DEFAULT_CONFIG));
      const extra = Object.fromEntries(Object.entries(cfg).filter(([k]) => !known.has(k)));
      setAdvancedJson(Object.keys(extra).length ? JSON.stringify(extra, null, 2) : '{}');
    } else {
      setForm({ name: '', kind: 'broadcast', config: { ...DEFAULT_CONFIG } });
      setAdvancedJson('{}');
    }
  }, [editing]);

  const saveMut = useMutation({
    mutationFn: () => {
      let advanced = {};
      try { advanced = JSON.parse(advancedJson || '{}'); }
      catch { throw new Error('Advanced JSON is invalid'); }
      const payload = {
        tenant_id: tenantId, name: form.name, kind: form.kind,
        config: { ...form.config, ...advanced },
      };
      if (isEdit) return api.put(`/video/templates/${editing.id}`, payload).then(r => r.data);
      return api.post('/video/templates', payload).then(r => r.data);
    },
    onSuccess: () => { toast.success(isEdit ? 'Template updated' : 'Template created'); onSaved(); },
    onError: (e) => toast.error(e.message || getErrorMessage(e)),
  });

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="bt-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit template' : 'New broadcast template'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Cathedral Sunday Service" data-testid="bt-dialog-name" />
            </div>
            <div>
              <Label className="text-xs">Kind</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="bt-dialog-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <MediaUpload value={form.config.logo_url} onChange={(url) => setForm({ ...form, config: { ...form.config, logo_url: url } })} purpose="logo" label="Logo" hint="PNG with a transparent background works best" testId="bt-dialog-logo" previewClassName="h-16" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Intro text</Label>
              <Input value={form.config.intro_text} onChange={(e) => setForm({ ...form, config: { ...form.config, intro_text: e.target.value } })} placeholder="Welcome to Faith World" />
            </div>
            <div>
              <Label className="text-xs">Outro text</Label>
              <Input value={form.config.outro_text} onChange={(e) => setForm({ ...form, config: { ...form.config, outro_text: e.target.value } })} placeholder="Subscribe for more sermons" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Lower-third color</Label>
              <Input type="color" value={form.config.lower_third_color} onChange={(e) => setForm({ ...form, config: { ...form.config, lower_third_color: e.target.value } })} data-testid="bt-dialog-lt-color" />
            </div>
            <div>
              <Label className="text-xs">Lower-third position</Label>
              <Select value={form.config.lower_third_position} onValueChange={(v) => setForm({ ...form, config: { ...form.config, lower_third_position: v } })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bottom">Bottom</SelectItem>
                  <SelectItem value="top">Top</SelectItem>
                  <SelectItem value="bottom-left">Bottom-left</SelectItem>
                  <SelectItem value="bottom-right">Bottom-right</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Captions style</Label>
              <Select value={form.config.captions_style} onValueChange={(v) => setForm({ ...form, config: { ...form.config, captions_style: v } })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="bold">Bold</SelectItem>
                  <SelectItem value="karaoke">Karaoke</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Advanced JSON config (optional)</Label>
            <Textarea
              rows={5}
              value={advancedJson}
              onChange={(e) => setAdvancedJson(e.target.value)}
              className="font-mono text-xs"
              data-testid="bt-dialog-advanced"
              placeholder='{"font_family": "Inter", "watermark_opacity": 0.4}'
            />
            <p className="text-xs text-slate-400 mt-1">Anything here is merged into the template config.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!form.name || saveMut.isPending} onClick={() => saveMut.mutate()} data-testid="bt-dialog-save">
            {saveMut.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
